<script setup lang="ts">
import { Globe, Pencil, Plus, ShieldBan, ShieldCheck, Trash2, User, Users, UserRoundCog } from "@lucide/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { toast } from "vue-sonner";
import {
  createBinding,
  createGrant,
  deleteBinding,
  deleteGrant,
  deleteRole,
  upsertRole,
  useDatasources,
  usePrincipals,
  useRoles,
  type BindingInput,
  type GrantInput,
} from "@/api/admin";
import type { Grant, RoleBinding } from "@/api/contracts";
import BindingForm from "@/components/admin/BindingForm.vue";
import GrantForm from "@/components/admin/GrantForm.vue";
import WhereChips from "@/components/admin/WhereChips.vue";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import FormError from "@/components/common/FormError.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import SearchInput from "@/components/common/SearchInput.vue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { confirm } from "@/composables/useConfirm";
import { useSession } from "@/composables/useSession";
import { t } from "@/copy/strings";
import { errorMessage } from "@/lib/errors";
import { formatDateTime } from "@/lib/format";

definePage({ meta: { title: "Role", requires: { adminArea: "access" } } });

const route = useRoute("/admin/access/roles/[id]");
const router = useRouter();
const queryClient = useQueryClient();
const { can } = useSession();
const roles = useRoles();
const role = computed(() => roles.data.value?.find((r) => r.id === route.params.id));
const principals = usePrincipals("", () => can("admin.users"));
const datasources = useDatasources(() => can("admin.datasources"));
const lineages = computed(() => [...new Set((datasources.data.value ?? []).map((d) => d.labels.lineage).filter(Boolean) as string[])].sort());

const grantOpen = ref(false);
const bindingOpen = ref(false);
const editOpen = ref(false);
const busy = ref(false);
const formError = ref<unknown>(null);
const editLabel = ref("");
const editDescription = ref("");

const allows = computed(() => (role.value?.grants ?? []).filter((g) => g.effect === "allow"));
const denies = computed(() => (role.value?.grants ?? []).filter((g) => g.effect === "deny"));
/** Long grant lists get a filter (capability, labels, obligations). */
const grantFilter = ref("");
const shownGrants = computed(() => {
  const needle = grantFilter.value.trim().toLowerCase();
  const list = [...denies.value, ...allows.value];
  return needle ? list.filter((g) => JSON.stringify([g.capability, g.where, g.obligations]).toLowerCase().includes(needle)) : list;
});

const SUBJECT = {
  user: { icon: User, label: "Person" },
  kc_group: { icon: Users, label: "Group" },
  kc_role: { icon: UserRoundCog, label: "Realm role" },
  everyone: { icon: Globe, label: "Everyone" },
};

function subjectName(b: RoleBinding): string {
  if (b.subjectType === "everyone") return "Everyone who can sign in";
  if (b.subjectType === "user") {
    const p = principals.data.value?.find((x) => x.subject === b.subject);
    return p ? `${p.displayName || p.username}` : b.subject;
  }
  return b.subject;
}

async function refresh() {
  await queryClient.invalidateQueries({ queryKey: ["admin", "roles"] });
}

async function addGrant(grant: GrantInput) {
  busy.value = true;
  formError.value = null;
  try {
    await createGrant(grant);
    toast.success(`Granted ${grant.capability}.`);
    grantOpen.value = false;
    await refresh();
  } catch (err) {
    formError.value = err;
  } finally {
    busy.value = false;
  }
}

async function removeGrant(g: Grant) {
  const answer = await confirm({ title: `Remove ${g.effect} ${g.capability}?`, description: "People with this role lose it right away.", confirmLabel: t.common.remove, destructive: true, reason: true });
  if (!answer) return;
  try {
    await deleteGrant(g.id, answer.reason);
    toast.success("Grant removed.");
    await refresh();
  } catch (err) {
    toast.error(errorMessage(err));
  }
}

async function addBinding(binding: BindingInput) {
  busy.value = true;
  formError.value = null;
  try {
    await createBinding(binding);
    toast.success("Role given.");
    bindingOpen.value = false;
    await refresh();
  } catch (err) {
    formError.value = err;
  } finally {
    busy.value = false;
  }
}

async function removeBinding(b: RoleBinding) {
  const answer = await confirm({ title: `Remove ${subjectName(b)} from ${role.value?.label}?`, confirmLabel: t.common.remove, destructive: true });
  if (!answer) return;
  try {
    await deleteBinding(b.id);
    toast.success("Removed.");
    await refresh();
  } catch (err) {
    toast.error(errorMessage(err));
  }
}

function openEdit() {
  editLabel.value = role.value?.label ?? "";
  editDescription.value = role.value?.description ?? "";
  formError.value = null;
  editOpen.value = true;
}

async function saveEdit() {
  if (!role.value) return;
  busy.value = true;
  try {
    await upsertRole(role.value.id, { label: editLabel.value.trim(), description: editDescription.value.trim() });
    editOpen.value = false;
    await refresh();
  } catch (err) {
    formError.value = err;
  } finally {
    busy.value = false;
  }
}

async function removeRole() {
  if (!role.value) return;
  const answer = await confirm({
    title: `Delete role ${role.value.label}?`,
    description: `Its ${role.value.grants.length} grants and ${role.value.bindings.length} bindings are deleted too.`,
    confirmLabel: t.common.delete,
    destructive: true,
    typeToConfirm: role.value.id,
  });
  if (!answer) return;
  try {
    await deleteRole(role.value.id);
    toast.success("Role deleted.");
    await refresh();
    await router.push("/admin/access");
  } catch (err) {
    toast.error(errorMessage(err));
  }
}
</script>

<template>
  <div>
    <ListSkeleton v-if="roles.isPending.value" :rows="6" />
    <ErrorState v-else-if="roles.error.value" :error="roles.error.value" @retry="roles.refetch()" />
    <EmptyState v-else-if="!role" title="Role not found" :description="`There is no role “${route.params.id}”.`">
      <Button as-child variant="outline"><RouterLink to="/admin/access">{{ t.admin.roles }}</RouterLink></Button>
    </EmptyState>
    <template v-else>
      <PageHeader :title="role.label" :description="role.description" :crumbs="[{ label: t.admin.title, to: '/admin' }, { label: t.nav.access, to: '/admin/access' }, { label: role.label }]">
        <template #badges>
          <Badge variant="muted" class="font-mono">{{ role.id }}</Badge>
          <Badge v-if="role.builtin" variant="muted">built-in</Badge>
        </template>
        <template #actions>
          <Button variant="outline" @click="openEdit"><Pencil /> {{ t.common.edit }}</Button>
          <Button v-if="role.id !== 'admin'" variant="ghost" class="text-destructive" @click="removeRole"><Trash2 /> {{ t.common.delete }}</Button>
        </template>
      </PageHeader>

      <div class="grid gap-8 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <section aria-labelledby="grants">
          <div class="mb-3 flex items-center gap-2">
            <h2 id="grants" class="section-title">{{ t.admin.grants }} <span class="font-normal text-muted-foreground">({{ role.grants.length }})</span></h2>
            <Button size="sm" class="ml-auto" @click="(formError = null), (grantOpen = true)"><Plus /> {{ t.admin.addGrant }}</Button>
          </div>
          <SearchInput v-if="role.grants.length > 12" v-model="grantFilter" placeholder="Filter grants…" class="mb-3" />
          <EmptyState v-if="role.grants.length === 0" compact :icon="ShieldCheck" title="No grants yet" description="Add what people with this role may do." />
          <EmptyState v-else-if="shownGrants.length === 0" compact :icon="ShieldCheck" :title="t.common.noResults" />
          <ul v-else class="surface flex flex-col divide-y overflow-hidden">
            <li v-for="g in shownGrants" :key="g.id" class="flex items-start gap-3 px-3 py-2.5 sm:px-4">
              <component :is="g.effect === 'deny' ? ShieldBan : ShieldCheck" :class="['mt-0.5 size-4 shrink-0', g.effect === 'deny' ? 'text-destructive' : 'text-success']" :aria-label="g.effect" />
              <div class="min-w-0 flex-1">
                <p class="flex flex-wrap items-center gap-2">
                  <code class="min-w-0 text-sm font-semibold [overflow-wrap:anywhere]">{{ g.capability }}</code>
                  <Badge v-if="g.effect === 'deny'" variant="danger">deny</Badge>
                  <span v-if="g.expiresAt" class="text-xs text-muted-foreground">until {{ formatDateTime(g.expiresAt) }}</span>
                </p>
                <WhereChips class="mt-1" :where="g.where" :obligations="g.obligations" />
              </div>
              <Button variant="ghost" size="icon-sm" :aria-label="`Remove grant ${g.capability}`" @click="removeGrant(g)"><Trash2 /></Button>
            </li>
          </ul>
        </section>

        <section aria-labelledby="bindings">
          <div class="mb-3 flex items-center gap-2">
            <h2 id="bindings" class="section-title">{{ t.admin.bindings }} <span class="font-normal text-muted-foreground">({{ role.bindings.length }})</span></h2>
            <Button size="sm" variant="outline" class="ml-auto" @click="(formError = null), (bindingOpen = true)"><Plus /> {{ t.admin.addBinding }}</Button>
          </div>
          <EmptyState v-if="role.bindings.length === 0" compact :icon="Users" title="Nobody has this role" description="Give it to a Keycloak group, a person or everyone." />
          <ul v-else class="surface flex flex-col divide-y overflow-hidden">
            <li v-for="b in role.bindings" :key="b.id" class="flex items-start gap-3 px-3 py-2.5 sm:px-4">
              <component :is="SUBJECT[b.subjectType].icon" class="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <div class="min-w-0 flex-1">
                <p class="text-sm font-medium break-all">{{ subjectName(b) }}</p>
                <p class="text-xs text-muted-foreground">
                  {{ SUBJECT[b.subjectType].label }}
                  <template v-if="b.expiresAt"> · until {{ formatDateTime(b.expiresAt) }}</template>
                  <template v-if="b.reason"> · {{ b.reason }}</template>
                </p>
              </div>
              <Button variant="ghost" size="icon-sm" :aria-label="`Remove ${subjectName(b)}`" @click="removeBinding(b)"><Trash2 /></Button>
            </li>
          </ul>
        </section>
      </div>

      <Sheet v-model:open="grantOpen">
        <SheetContent side="right" class="w-full overflow-y-auto sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>{{ t.admin.addGrant }}: {{ role.label }}</SheetTitle>
            <SheetDescription>You can only grant what you hold yourself (unless you are a superadmin).</SheetDescription>
          </SheetHeader>
          <div class="px-4 pb-6">
            <GrantForm v-if="grantOpen" :role-id="role.id" :busy="busy" :error="formError" :lineages="lineages" @submit="addGrant" @cancel="grantOpen = false" />
          </div>
        </SheetContent>
      </Sheet>

      <Sheet v-model:open="bindingOpen">
        <SheetContent side="right" class="w-full overflow-y-auto sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>Give “{{ role.label }}” to…</SheetTitle>
            <SheetDescription>Changes apply on the next request; no sign-out needed.</SheetDescription>
          </SheetHeader>
          <div class="px-4 pb-6">
            <BindingForm v-if="bindingOpen" :role-id="role.id" :busy="busy" :error="formError" @submit="addBinding" @cancel="bindingOpen = false" />
          </div>
        </SheetContent>
      </Sheet>

      <Dialog v-model:open="editOpen">
        <DialogContent>
          <DialogHeader><DialogTitle>Edit {{ role.id }}</DialogTitle></DialogHeader>
          <form class="grid gap-4" @submit.prevent="saveEdit">
            <div class="grid gap-1.5">
              <Label for="e-label">Name</Label>
              <Input id="e-label" v-model="editLabel" />
            </div>
            <div class="grid gap-1.5">
              <Label for="e-desc">Description</Label>
              <Textarea id="e-desc" v-model="editDescription" rows="3" />
            </div>
            <FormError :error="formError" />
            <DialogFooter>
              <Button type="button" variant="ghost" @click="editOpen = false">{{ t.common.cancel }}</Button>
              <Button type="submit" :disabled="busy || !editLabel.trim()">{{ t.common.save }}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </template>
  </div>
</template>
